import type { PublicProfile } from '@/types'
import ProfileBranding from '@/components/profile/ProfileBranding'
import { Mail, Phone, Download, ExternalLink } from 'lucide-react'

interface CreativeTemplateProps {
  profile: PublicProfile
}

export default function CreativeTemplate({ profile }: CreativeTemplateProps) {
  const { visibility } = profile.profileSettings
  const fullName = `${profile.firstName} ${profile.lastName}`
  const initials = `${profile.firstName.charAt(0)}${profile.lastName.charAt(0)}`.toUpperCase()

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Hero - Magazine style with huge typography */}
      <div className="relative overflow-hidden">
        {/* Colorful gradient background */}
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(45deg, var(--color-primary) 0%, var(--color-primary-dark) 50%, #000 100%)`,
          }}
        ></div>

        {/* Grid pattern overlay */}
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '50px 50px',
          }}
        ></div>

        <div className="container-custom relative z-10 py-20 md:py-32">
          <div
            className={`grid grid-cols-1 ${visibility.showPhoto ? 'lg:grid-cols-2' : ''} gap-16 items-center`}
          >
            <div className={!visibility.showPhoto ? 'max-w-3xl mx-auto text-center' : ''}>
              {/* Name with creative split */}
              <div className="mb-8">
                <div className="overflow-hidden">
                  <h1 className="text-8xl md:text-9xl font-black leading-none mb-2 transform -rotate-2">
                    {profile.firstName}
                  </h1>
                </div>
                <div className="overflow-hidden">
                  <h1 className="text-8xl md:text-9xl font-black leading-none transform rotate-1">
                    {profile.lastName}
                  </h1>
                </div>
              </div>

              {profile.title && (
                <div className="inline-block transform -rotate-1">
                  <p className="text-2xl md:text-3xl font-bold px-6 py-3 bg-white text-black mb-8">
                    {profile.title}
                  </p>
                </div>
              )}

              {profile.bio && (
                <p className="text-xl leading-relaxed text-white/90 max-w-xl">{profile.bio}</p>
              )}
            </div>

            {/* Profile photo with creative frame */}
            {visibility.showPhoto && (
              <div className="flex justify-center lg:justify-end">
                <div className="relative">
                  {/* Colorful background shapes */}
                  <div className="absolute -top-8 -left-8 w-64 h-64 bg-yellow-400 rounded-full blur-3xl opacity-50"></div>
                  <div className="absolute -bottom-8 -right-8 w-64 h-64 bg-pink-500 rounded-full blur-3xl opacity-50"></div>

                  <div className="relative w-72 h-72 md:w-96 md:h-96">
                    {profile.profilePhoto ? (
                      <img
                        src={profile.profilePhoto}
                        alt={fullName}
                        className="w-full h-full object-cover transform rotate-3 shadow-2xl"
                        style={{ clipPath: 'polygon(10% 0%, 100% 0%, 90% 100%, 0% 100%)' }}
                      />
                    ) : (
                      <div
                        className="w-full h-full flex items-center justify-center text-9xl font-black transform rotate-3 shadow-2xl"
                        style={{
                          clipPath: 'polygon(10% 0%, 100% 0%, 90% 100%, 0% 100%)',
                          background: 'linear-gradient(135deg, #FFD700, #FF6B6B)',
                        }}
                      >
                        {initials}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main content with magazine layout */}
      <div className="bg-white text-black">
        <div className="container-custom py-20">
          {/* Experience - Bold cards */}
          {visibility.showExperience && profile.experience && profile.experience.length > 0 && (
            <section className="mb-20">
              <h2 className="text-6xl md:text-7xl font-black mb-12 transform -rotate-1 inline-block">
                Experience
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {profile.experience.map((exp, index) => (
                  <div
                    key={index}
                    className="p-8 border-4 border-black transform hover:scale-105 transition-transform"
                    style={{
                      backgroundColor: index % 2 === 0 ? 'var(--color-primary-light)' : '#FFF9C4',
                    }}
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-2xl font-black">{exp.jobTitle}</h3>
                        {exp.isCurrent && (
                          <span className="text-xs font-bold bg-green-500 text-white px-2 py-1">
                            CURRENT
                          </span>
                        )}
                      </div>
                      <span className="text-sm font-bold bg-black text-white px-3 py-1 whitespace-nowrap ml-4">
                        {exp.startDate} - {exp.isCurrent ? 'Present' : exp.endDate}
                      </span>
                    </div>
                    <p
                      className="text-xl font-bold mb-3"
                      style={{ color: 'var(--color-primary-dark)' }}
                    >
                      {exp.companyName}
                    </p>
                    {exp.description && (
                      <p className="text-gray-700 leading-relaxed">{exp.description}</p>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Projects - Creative cards */}
          {visibility.showProjects && profile.projects && profile.projects.length > 0 && (
            <section className="mb-20">
              <h2 className="text-6xl md:text-7xl font-black mb-12 transform rotate-1 inline-block">
                Projects
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {profile.projects.map((project, index) => (
                  <div
                    key={index}
                    className="p-8 border-4 border-black transform hover:scale-105 transition-transform"
                    style={{
                      backgroundColor:
                        index % 3 === 0 ? '#E3F2FD' : index % 3 === 1 ? '#F3E5F5' : '#FFF9C4',
                    }}
                  >
                    <div className="flex items-start justify-between mb-4 flex-wrap gap-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-2xl font-black">{project.title}</h3>
                        {project.isCurrent && (
                          <span className="text-xs font-bold bg-green-500 text-white px-2 py-1">
                            CURRENT
                          </span>
                        )}
                      </div>
                      {(project.startDate || project.endDate) && (
                        <span className="text-sm font-bold bg-black text-white px-3 py-1 whitespace-nowrap">
                          {project.startDate || 'N/A'} -{' '}
                          {project.isCurrent ? 'Present' : project.endDate || 'N/A'}
                        </span>
                      )}
                    </div>
                    {project.description && (
                      <p className="text-gray-700 leading-relaxed mb-4">{project.description}</p>
                    )}
                    {project.technologies && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {project.technologies.split(',').map((tech, i) => (
                          <span
                            key={i}
                            className="px-3 py-1 text-xs font-bold bg-white border-2 border-black"
                          >
                            {tech.trim()}
                          </span>
                        ))}
                      </div>
                    )}
                    {(project.projectUrl || project.repositoryUrl) && (
                      <div className="flex gap-3 pt-4 border-t-2 border-black/20">
                        {project.projectUrl && (
                          <a
                            href={project.projectUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm font-bold underline hover:no-underline"
                            style={{ color: 'var(--color-primary-dark)' }}
                          >
                            LIVE DEMO →
                          </a>
                        )}
                        {project.repositoryUrl && (
                          <a
                            href={project.repositoryUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm font-bold underline hover:no-underline"
                            style={{ color: 'var(--color-primary-dark)' }}
                          >
                            SOURCE CODE →
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Two column grid for Education, Skills, Languages */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-20">
            {/* Education */}
            {visibility.showEducation && profile.education && profile.education.length > 0 && (
              <div className="lg:col-span-2">
                <h2 className="text-5xl font-black mb-8 transform rotate-1 inline-block">
                  Education
                </h2>
                <div className="space-y-6">
                  {profile.education.map((edu, index) => (
                    <div
                      key={index}
                      className="p-6 bg-gradient-to-br from-purple-100 to-pink-100 border-l-8"
                      style={{ borderColor: 'var(--color-primary)' }}
                    >
                      <h3 className="text-2xl font-black mb-2">{edu.degree}</h3>
                      <p
                        className="text-lg font-bold"
                        style={{ color: 'var(--color-primary-dark)' }}
                      >
                        {edu.institution}
                      </p>
                      {edu.fieldOfStudy && <p className="text-gray-600 mt-1">{edu.fieldOfStudy}</p>}
                      <p className="text-sm font-bold mt-2">
                        {edu.startDate} - {edu.endDate}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Skills & Languages stacked */}
            <div className="space-y-8">
              {/* Skills */}
              {visibility.showSkills && profile.skills && profile.skills.length > 0 && (
                <div>
                  <h2 className="text-4xl font-black mb-6 transform -rotate-2 inline-block">
                    Skills
                  </h2>
                  <div className="flex flex-wrap gap-3">
                    {profile.skills.map((skill, index) => (
                      <span
                        key={index}
                        className="px-4 py-2 font-bold text-sm border-2 border-black bg-yellow-300 hover:bg-yellow-400 transition-colors"
                      >
                        {skill.skill?.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Languages */}
              {visibility.showLanguages && profile.languages && profile.languages.length > 0 && (
                <div>
                  <h2 className="text-4xl font-black mb-6 transform rotate-2 inline-block">
                    Languages
                  </h2>
                  <div className="space-y-2">
                    {profile.languages?.map((lang, index) => (
                      <div
                        key={index}
                        className="flex justify-between items-center p-3 bg-black text-white font-bold"
                      >
                        <span>{lang.language?.name}</span>
                        <span className="text-yellow-300">{lang.proficiencyLevel}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Contact - Full width colorful bar */}
          {visibility.showContact && (profile.email || profile.phone) && (
            <div
              className="p-10 border-8 border-black"
              style={{
                background: 'linear-gradient(135deg, #FF6B6B, #4ECDC4, #FFD700)',
              }}
            >
              <h2 className="text-5xl font-black text-white mb-6 transform -rotate-1 inline-block drop-shadow-lg">
                Get In Touch
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {profile.email && (
                  <a
                    href={`mailto:${profile.email}`}
                    className="flex items-center gap-3 p-5 bg-white text-black font-bold hover:scale-105 transition-transform border-4 border-black"
                  >
                    <Mail className="w-6 h-6" />
                    <span className="break-all">{profile.email}</span>
                  </a>
                )}
                {profile.phone && (
                  <a
                    href={`tel:${profile.phone}`}
                    className="flex items-center gap-3 p-5 bg-white text-black font-bold hover:scale-105 transition-transform border-4 border-black"
                  >
                    <Phone className="w-6 h-6" />
                    <span>{profile.phone}</span>
                  </a>
                )}
              </div>

              {/* Social links */}
              <div className="flex flex-wrap gap-4 mt-6">
                {profile.linkedInUrl && (
                  <a
                    href={profile.linkedInUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-6 py-3 bg-black text-white font-bold hover:bg-white hover:text-black transition-colors border-4 border-black flex items-center gap-2"
                  >
                    LinkedIn
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
                {profile.githubUrl && (
                  <a
                    href={profile.githubUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-6 py-3 bg-black text-white font-bold hover:bg-white hover:text-black transition-colors border-4 border-black flex items-center gap-2"
                  >
                    GitHub
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
                {profile.portfolioUrl && (
                  <a
                    href={profile.portfolioUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-6 py-3 bg-black text-white font-bold hover:bg-white hover:text-black transition-colors border-4 border-black flex items-center gap-2"
                  >
                    Portfolio
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Documents */}
          {visibility.showDocuments && profile.cvUrl && (
            <div className="mt-16 text-center">
              <a
                href={profile.cvUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 px-12 py-6 bg-black text-white text-2xl font-black hover:scale-110 transition-transform border-8 border-black hover:bg-yellow-300 hover:text-black"
              >
                <Download className="w-8 h-8" />
                <span>Download My CV</span>
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Subtle Branding */}
      <ProfileBranding variant="light" />
    </div>
  )
}
